import React from 'react'
import cx from 'classnames'
import { List } from 'immutable'
import { connect } from 'react-redux'
import styles from './Routes.scss'
import { ExperienceRoute } from './ExperienceRoute'
import { SectionBox } from 'common/styleguide/SectionBox'
import { CTA } from 'common/styleguide/CTA'
import { Icon } from 'common/Icon'
import { SimpleMap } from 'common/SimpleMap'
import { experienceRoutesActions, experienceRoutesSelectors } from 'lib/api/experiences/routes'
import { experienceActions } from 'data/resources/experiences'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { LoadExistingContainer, resultTypes } from 'features/search/LoadExisting'
import { compose } from 'redux'
import Switch from 'react-switch'
import { ConfirmUnsetShowRoutesDialog, CONFIRM_UNSET_SHOW_ROUTES } from './ConfirmUnsetShowRoutesDialog'
import { withMetricUnits } from 'common/units/MetricUnitsContext.js'

export class _Routes extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      showLoadExisting: false,
      dialog: null
    }
  }

  componentDidMount () {
    const { loadRoutes, experience } = this.props
    loadRoutes(experience.id)
  }

  componentDidUpdate (prevProps) {
    const { loadRoutes, experience } = this.props
    if (prevProps.experience.id !== experience.id) {
      loadRoutes(experience.id)
    }
  }

  static getDerivedStateFromProps (props, state) {
    const { experienceRouteIds } = props
    const { selectedExperienceRouteId } = state

    if (experienceRouteIds &&
      experienceRouteIds.size &&
      (selectedExperienceRouteId === undefined || !experienceRouteIds.toJS().concat([-1]).includes(selectedExperienceRouteId))) {
      return {
        selectedExperienceRouteId: experienceRouteIds.first()
      }
    } else {
      return null
    }
  }

  addRoute = (routeId) => {
    if (!this.props.experienceRoutes.map(er => er.routeId).includes(routeId)) {
      this.props.addRoute(this.props.experience.id, routeId)
    }
  }

  onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const { experienceRouteIds, reorder, experience } = this.props

    // Move the id from the old position to the new one
    const newIds = experienceRouteIds.toJS()
    const [removed] = newIds.splice(result.source.index, 1)
    newIds.splice(result.destination.index, 0, removed)

    reorder(experience.id, List(newIds))
  }

  showAllOnMap = () => {
    this.setState({
      selectedExperienceRouteId: -1
    })
  }

  toggleSwitch = () => {
    const { experience, updateExperience } = this.props
    const { showRoutes } = experience

    if (showRoutes) {
      this.setState({ dialog: CONFIRM_UNSET_SHOW_ROUTES })
    } else {
      const updatedExperience = experience.merge({ showRoutes: true })
      updateExperience(updatedExperience)
    }
  }

  unsetShowRoutes = () => {
    const { experience, updateExperience } = this.props
    const updatedExperience = experience.merge({ showRoutes: false })

    return updateExperience(updatedExperience)
  }

  render () {
    const {
      preview,
      editable,
      showSwitch,
      experience,
      experienceRouteIds,
      experienceRoutes,
      experienceRoute,
      routes,
      deleteExperienceRoute,
      metricUnits
    } = this.props
    const { showLoadExisting, selectedExperienceRouteId, dialog } = this.state

    if (!experienceRouteIds) {
      return <div />
    }

    const mapRouteIds = experienceRoutes.size && (selectedExperienceRouteId === -1
      ? experienceRoutes.map(er => er.get('routeId')).valueSeq().toJS()
      : [experienceRoute(selectedExperienceRouteId).routeId])

    const belowTitle = (
      <div className={styles.belowTitle}>
        {!preview && editable && <div>
          <CTA primary margins={false} onClick={() => this.setState({ showLoadExisting: true })}>
            <Icon i='ios-pin' /> Import routes
          </CTA>
        </div>}
        {showSwitch && <label className={styles.switchLabel}>
          <Switch
            checked={experience.showRoutes}
            onChange={this.toggleSwitch}
            onColor={'#FA6400'}
            height={18}
            width={30}
            checkedIcon={false}
            uncheckedIcon={false}
            type='checkbox'
            id='showRoutesSection' />
          <span className={styles.switchSpan}>Show Route Section in App</span>
        </label>}
      </div>
    )

    return (
      <div>
        {dialog === CONFIRM_UNSET_SHOW_ROUTES &&
          <ConfirmUnsetShowRoutesDialog
            onConfirm={this.unsetShowRoutes}
            onRequestClose={() => this.setState({ dialog: null })} />}
        <SectionBox
          title='Routes'
          belowTitle={belowTitle}
          noPadding
          className={cx(styles.card, preview && styles.previewCard, !preview && styles.full)}>
          <div className={styles.routePanel}>
            {showLoadExisting &&
              <LoadExistingContainer
                loadRoute={this.addRoute}
                onRequestClose={() => this.setState({ showLoadExisting: false })}
                routesOnly
                allowedResultTypes={[resultTypes.USER]}
                submitLabel='Add to Experience' />}
            <div className={styles.routeList}>
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId='droppable'>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef}>
                      {experienceRouteIds.map((id, index, a) =>
                        <DraggableExperienceRoute
                          key={id}
                          id={id}
                          preview={preview}
                          editable={editable}
                          onSelect={() => this.setState({ selectedExperienceRouteId: id })}
                          selected={!preview && selectedExperienceRouteId === id}
                          experienceRoute={experienceRoute(id)}
                          route={routes.get(experienceRoute(id).routeId)}
                          index={index}
                          hoverDisabled={preview || snapshot.isDraggingOver}
                          deleteExperienceRoute={deleteExperienceRoute} />
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {preview && editable && <>
                <CTA secondary inline to={`/experiences/${experience.id}/routes`}>
                  Edit Routes
                </CTA>
                <CTA link inline href={`/experiences/${experience.id}/print`}>
                  Print Routes
                </CTA>
              </>
              }
            </div>
            {!preview && experienceRouteIds.size > 0 && <div className={styles.showAllOnMapContainer}>
              <a href='#' onClick={this.showAllOnMap} className={styles.showAllOnMap}>Show All on Map</a>
            </div>}
          </div>
          {!preview && experienceRouteIds.size > 0 && <div className={styles.mapPanel}>
            <SimpleMap
              experimental
              showNativeFullscreen
              height='100%'
              mapSettings={{ scrollWheelZoom: false }}
              defaultOverlay={'rwgps'}
              routeIds={mapRouteIds}
              sampleGraph
              metricUnits={metricUnits} />
          </div>}
        </SectionBox>
      </div>
    )
  }
}

class DraggableExperienceRoute extends React.PureComponent {
  render () {
    const { index, id, experienceRoute, route, deleteExperienceRoute, hoverDisabled, onSelect, selected, preview, editable } = this.props

    return (
      <Draggable draggableId={`draggableExperienceRoute-${id}`} index={index}>{(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <ExperienceRoute
            experienceRoute={experienceRoute}
            route={route}
            onSelect={onSelect}
            selected={selected}
            editable={editable}
            preview={preview}
            deleteExperienceRoute={deleteExperienceRoute}
            hoverDisabled={hoverDisabled}
            isDragging={snapshot.isDragging}
            dragHandleProps={provided.dragHandleProps} />
        </div>
      )}
      </Draggable>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { experience } = ownProps

  return {
    experienceRouteIds: state.routes.getIn(['orderedExperienceRouteIds', experience.id]),
    experienceRoutes: experienceRoutesSelectors.experienceRoutesForExperience(state, experience.id),
    experienceRoute: (id) => experienceRoutesSelectors.find(state, id),
    routes: state.routes.get('routes')
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadRoutes: compose(dispatch, experienceRoutesActions.loadAll),
  addRoute: compose(dispatch, experienceRoutesActions.addRoute),
  deleteExperienceRoute: compose(dispatch, experienceRoutesActions.delete),
  reorder: compose(dispatch, experienceRoutesActions.reorder),
  updateExperience: compose(dispatch, experienceActions.update)
})

export const Routes = withMetricUnits(connect(
  mapStateToProps,
  mapDispatchToProps
)(_Routes))
